import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Camera as CameraIcon } from "lucide-react";
import jsQR from "jsqr";

/**
 * iter80.1: Mobile QR-Code Scanner via getUserMedia + jsQR.
 * - Öffnet einen Live-Kamera-Overlay (Rückkamera bevorzugt).
 * - Erkennt QR-Code automatisch und ruft `onScan(text)` genau einmal auf.
 * - Fallback bei Kamera-Denial: Fehlermeldung anzeigen; User kann manuell eingeben.
 */
export default function QRScanner({ open, onScan, onClose }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const scannedRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    scannedRef.current = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          tick();
        }
      } catch (e) {
        setError(t("qr_scanner.camera_error"));
      }
    };

    const tick = () => {
      if (scannedRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
      if (code && code.data) {
        scannedRef.current = true;
        stop();
        onScan(code.data);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    start();
    return () => stop();
  }, [open, onScan]);

  if (!open) return null;

  return (
    <div data-testid="qr-scanner-overlay" className="fixed inset-0 z-[70] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <CameraIcon size={18} />
          <p className="text-sm font-semibold">QR-Code scannen</p>
        </div>
        <button data-testid="qr-scanner-close" onClick={onClose} className="p-1 text-white/80 hover:text-white">
          <X size={22} />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
              <p className="text-sm text-white/90">{error}</p>
              <button
                data-testid="qr-scanner-manual-close"
                onClick={onClose}
                className="mt-4 h-10 px-4 bg-white text-black text-sm font-medium">
                Schließen
              </button>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
            {/* Scan-Overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-64 border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
            </div>
            <p className="absolute bottom-6 left-0 right-0 text-center text-xs text-white/80">
              QR-Code in den Rahmen halten — Erkennung passiert automatisch.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
